import React, { useCallback, useEffect, useState } from "react";

import { faGripVertical } from "@fortawesome/pro-solid-svg-icons/faGripVertical";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tag from "antd/es/tag";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { DraggableProvidedDragHandleProps, DroppableId } from "react-beautiful-dnd";
import { defineMessages, useIntl } from "react-intl";

import { CategoryFieldType } from "../../type/CategorizedField";
import SchemaMap from "../../type/SchemaMap";

import styles from "./PaletteFieldName.module.scss";

interface Props {
  categoryField: CategoryFieldType;
  dragging?: boolean;
  draggingOver?: DroppableId | null;
  dragHandleProps?: DraggableProvidedDragHandleProps | null;
  locked?: boolean;
  onDoubleClick?: () => void;
  schemaMap: SchemaMap;
}

const messages = defineMessages({
  drag: {
    id: "settings.formLayouts.modal.fieldName.drag",
    defaultMessage: "Drag field into layout",
    description: "Drag field into layout tooltip",
  },
  inUse: {
    id: "settings.formLayouts.modal.properties.tag.inUse",
    defaultMessage: "In Use",
    description: "In Use tag",
  },
  systemRequired: {
    id: "settings.formLayouts.modal.properties.tag.systemRequired",
    defaultMessage: "System Required",
    description: "System Required tag",
  },
});

const PaletteFieldName: React.FC<Props> = ({
  categoryField,
  dragging,
  draggingOver,
  dragHandleProps,
  locked,
  onDoubleClick,
  schemaMap,
}) => {
  const intl = useIntl();
  const { disabled, displayName, field, required } = categoryField;
  const tooltipDisabled = disabled || locked || dragging;
  const [visible, setVisible] = useState(false);

  const handleDoubleClick = useCallback(() => {
    if (!disabled) {
      onDoubleClick?.();
    }
  }, [disabled, onDoubleClick]);

  const handleVisibleChange = useCallback(
    (value: boolean) => {
      setVisible(value);
    },
    [setVisible]
  );

  useEffect(() => {
    if (tooltipDisabled) {
      setVisible(false);
    }
  }, [setVisible, tooltipDisabled]);

  return (
    <div
      {...dragHandleProps}
      className={cn(styles.container, {
        [styles.disabled]: !!disabled,
        [styles.dragging]: dragging,
        [styles.locked]: locked,
        [styles.over]: dragging && draggingOver === "canvas",
      })}
      onDoubleClick={!dragging ? handleDoubleClick : undefined}
    >
      <Tooltip
        onVisibleChange={handleVisibleChange}
        title={intl.formatMessage(messages.drag)}
        trigger={tooltipDisabled ? [] : ["hover"]}
        visible={visible && !tooltipDisabled}
      >
        <div className={styles.inner}>
          <div className={styles.handle} tabIndex={-1}>
            <FontAwesomeIcon icon={faGripVertical} />
          </div>

          <div className={styles.name}>{displayName}</div>

          {!locked && !dragging && disabled && (
            <>
              {required ? (
                <Tag color="default">{intl.formatMessage(messages.systemRequired)}</Tag>
              ) : schemaMap.has(field) ? (
                <Tag color="default">{intl.formatMessage(messages.inUse)}</Tag>
              ) : null}
            </>
          )}
        </div>
      </Tooltip>
    </div>
  );
};

export default PaletteFieldName;
